// ═══════════════════════════════════════════════════════════════════════════
// LIRE LE FICHIER DES TAUX DE L'ONSS — LA COTISATION FSE, PAR LA SOURCE
//
// POURQUOI CE MODULE EXISTE. Jusqu'au 25/08/2026, chaque taux de fonds de
// sécurité d'existence se cherchait CCT par CCT : ouvrir la convention,
// trouver la clause, lire le pourcentage, recommencer. Mesure de la campagne :
// 58 dossiers instruits pour 174 CP, dont 7 taux réellement confirmés — et
// deux campagnes entières de « confirmations » qui se sont révélées fausses
// (cf. l'en-tête de `fonds-se-statut.js`).
//
// Ce n'était pas la bonne source. **C'est l'ONSS qui PERÇOIT ces cotisations**
// — instructions administratives, « Les fonds de sécurité d'existence » :
// « La plupart des secteurs d'activité ont confié à l'ONSS la mission de
// percevoir les cotisations qui sont destinées aux Fonds de sécurité
// d'existence. » Elle publie donc ses propres taux, trimestre par trimestre,
// dans un fichier machine avec son XSD. Un percepteur ne se trompe pas sur ce
// qu'il perçoit.
//
// ⚠️ LE TAUX N'EST PAS ATTACHÉ À LA CP, MAIS À LA **CATÉGORIE D'EMPLOYEUR**.
// L'ONSS l'écrit : « Les cotisations perçues par l'ONSS au profit des
// différents Fonds de sécurité d'existence sont fonction de l'indice de
// catégorie de l'employeur ». Le pont CP ↔ catégorie se lit dans l'annexe
// structurée n° 27, dont les descriptions citent la CP en toutes lettres.
// C'est un pont TEXTUEL, donc faillible : il se mesure, il ne se suppose pas.
//
// ⚠️ ET IL N'Y A PAS UN TAUX, IL Y EN A PLUSIEURS. Une même catégorie porte
// jusqu'à onze codes travailleur distincts (FSE ouvriers, pension sectorielle
// ouvriers, forfaits, FSE employés, fonds CPNAE, commerce de détail…). Écraser
// tout ça par un seul nombre serait refaire l'erreur que ce module corrige :
// on rend les LIGNES, chacune avec son code et son intitulé officiel, et
// l'appelant choisit selon qu'il paie un ouvrier ou un employé.
//
// Sources primaires, lues le 25/08/2026 :
//   · Taux    : https://www.socialsecurity.be/site_fr/employer/applics/dmfa/documents/zip/dmfa_rate_2026_3.zip
//               (NOSSContributionRate_2026_3.xml + ContributionRateFile.xsd)
//   · Codes   : annexe structurée n° 2  « Liste des codes travailleurs pour
//               lesquels des cotisations sont dues », version 2026/1
//   · Pont CP : annexe structurée n° 27 « Liste des indices des différentes
//               catégories d'employeurs », version 2025/3
//   · Récit   : https://www.socialsecurity.be/site_fr/Infos/instructs/documents/xls/FSE_3-2026.xlsx
//   Toutes accessibles à un fetch nu avec UA Chrome (cf. carte d'accès, R2 7 bis).
//
// Ce fichier est PUR : il ne lit ni réseau ni disque. Le réseau vit dans
// `scripts/veille/moisson-onss-fse.mjs`, l'instantané dans `veille/onss-fse.json`.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Les codes travailleur qui portent une cotisation de fonds de sécurité
 * d'existence. Intitulés recopiés de l'annexe ONSS n° 2 (version 2026/1),
 * abrégés — le texte intégral vit dans l'annexe, pas ici.
 */
export const CODES_FSE = {
  820: { public: 'ouvrier',  forfait: false, libelle: "Fonds de sécurité d'existence, sur les rémunérations à 108 % des travailleurs manuels" },
  825: { public: 'ouvrier',  forfait: false, libelle: 'Fonds de pension sectoriel pour ouvriers' },
  826: { public: 'ouvrier',  forfait: true,  libelle: "Fonds de sécurité d'existence — cotisation forfaitaire ouvriers" },
  827: { public: 'ouvrier',  forfait: true,  libelle: 'Fonds de pension sectoriel ouvriers — cotisation forfaitaire' },
  830: { public: 'employe',  forfait: false, libelle: "Fonds de sécurité d'existence (autres que CPAE 200, commerce de détail 201 et socio-culturel)" },
  831: { public: 'employe',  forfait: false, libelle: 'Fonds Social C.P.A.E. (CP 200) depuis le 01/04/2015' },
  832: { public: 'employe',  forfait: false, libelle: 'Fonds Social du commerce de détail indépendant (CP 201)' },
  833: { public: 'employe',  forfait: false, libelle: 'Fonds social du secteur socio-culturel des Communautés française et germanophone' },
  835: { public: 'employe',  forfait: false, libelle: 'Fonds de pension sectoriel pour employés' },
  836: { public: 'employe',  forfait: true,  libelle: "Fonds de sécurité d'existence — cotisation forfaitaire employés" },
  837: { public: 'employe',  forfait: true,  libelle: 'Fonds de pension sectoriel employés — cotisation forfaitaire' },
};

/** Vrai si ce code travailleur porte une cotisation FSE. */
export function estCodeFse(code) {
  return Object.prototype.hasOwnProperty.call(CODES_FSE, String(Number(code)));
}

/** Normalise « CP 140.01 », « 140,01 », « scp140.01 », 140.01 → « 140.01 ». */
export function cleCp(cp) {
  return String(cp ?? '').trim().replace(/^s?\.?c\.?p\.?\s*n?[°o]?\s*/i, '').replace(',', '.');
}

/**
 * Extrait les lignes FSE d'un `NOSSContributionRate_<trim>.xml` de l'ONSS.
 * Rend un tableau plat, une entrée par (catégorie, code, type de cotisation).
 *
 * On ne fait AUCUNE somme ici. Deux lignes d'une même catégorie peuvent être
 * deux régimes exclusifs (type 0 = régime général, type 5 = « 50 travailleurs
 * et plus » dans l'horeca) : les additionner inventerait un taux que personne
 * ne paie.
 */
export function lignesFseDepuisXml(xml) {
  const texte = String(xml ?? '');
  const lignes = [];
  const champ = (bloc, nom) => {
    const m = bloc.match(new RegExp(`<${nom}>([\\s\\S]*?)</${nom}>`));
    return m ? m[1].trim() : '';
  };
  for (const m of texte.matchAll(/<ContributionRate>([\s\S]*?)<\/ContributionRate>/g)) {
    const bloc = m[1];
    const code = champ(bloc, 'ContributionWorkerCode').replace(/^0+(?=\d)/, '');
    if (!estCodeFse(code)) continue;
    const patronal = Number(champ(bloc, 'EmployerRate'));
    lignes.push({
      categorie: champ(bloc, 'EmployerClass'),
      code,
      typeCotisation: champ(bloc, 'ContributionType'),
      patronal: Number.isFinite(patronal) ? patronal : null,
      personnel: Number(champ(bloc, 'PersonalRate')) || 0,
      forfait: champ(bloc, 'IsFlatRate') === 'true',
    });
  }
  return lignes;
}

/** Le trimestre déclaré dans l'en-tête du fichier ONSS, ex. « 20263 ». */
export function trimestreDepuisXml(xml) {
  const m = String(xml ?? '').match(/<Quarter>(\d{5})<\/Quarter>/);
  return m ? m[1] : null;
}

// Un numéro de CP cité dans une description d'annexe 27. On exige un mot
// déclencheur (« CP », « commission paritaire »…) AVANT le nombre : sans lui,
// on ramasserait les années, les numéros d'arrêté et les seuils de personnel.
const DECLENCHEUR = /(?:C\.?\s?P\.?|commissions?\s+paritaires?|sous-commissions?\s+paritaires?|SCP)[^0-9.;]{0,60}?(\d{3}(?:\.\d{2})?)/gi;
const NUMERO = /n[°o]\s*(\d{3}(?:\.\d{2})?)/g;

// ⛔ UNE CP NOMMÉE POUR ÊTRE EXCLUE N'EST PAS UNE CP RATTACHÉE — et c'est un
// piège qui coûte cher, parce qu'il ne ressemble pas à une erreur : il produit
// une catégorie de PLUS, donc une ambiguïté, donc un taux `null`, donc une
// fiche qui reste muette alors que la réponse était unique.
//
// Cas réel (25/08/2026, mesuré) : la catégorie 039 « personnel de maison »
// écrit « en sont exclus les employeurs, qui relèvent des Commissions
// paritaires n° 144 de l'agriculture et n° 145 pour les entreprises
// horticoles ». Le pont textuel y lisait deux rattachements. Résultat : la
// CP 144, dont l'ONSS ne publie QU'UNE catégorie (193, à 10,58 %), passait
// pour ambiguë entre 0,10 % et 10,58 % — et sa fiche est restée sur un 4 %
// sans source pendant tout l'audit.
//
// 📏 Portée mesurée sur l'instantané 2026/3 : 1 catégorie sur 232 porte une
// clause d'exclusion nommant une CP, pour 2 rattachements faux (144 et 145).
// Petite portée, gros effet — d'où le garde-fou plutôt qu'une note.
const CLAUSE_EXCLUSION = /(?:en sont exclus|à l'exclusion de|a l'exclusion de)[\s\S]*?(?=\s*\(voir|;|$)/gi;

/** La description privée de ses clauses d'exclusion. Exportée pour être testée
 *  seule : c'est elle qui décide de ce que le pont voit. */
export function sansClausesDExclusion(description) {
  return String(description ?? '').replace(CLAUSE_EXCLUSION, ' ');
}

/**
 * Les CP citées par une description de catégorie d'employeur (annexe 27).
 *
 * ⚠️ Ne rend QUE ce que le texte dit. Une description sans numéro rend un
 * tableau vide — ce n'est pas « pas de CP », c'est « l'annexe ne le dit pas ».
 * Sur les 232 catégories actives, 63 sont dans ce cas et la plupart n'ont
 * légitimement aucune CP (catégorie générale, ambassades, médecins…).
 */
export function cpDepuisDescription(description) {
  const texte = sansClausesDExclusion(description);
  const trouvees = new Set();
  for (const re of [DECLENCHEUR, NUMERO]) {
    re.lastIndex = 0;
    for (const m of texte.matchAll(re)) {
      const n = Number(m[1].split('.')[0]);
      if (n >= 100 && n <= 400) trouvees.add(m[1]);
    }
  }
  return [...trouvees].sort();
}

/**
 * Assemble l'index CP → lignes FSE, à partir des lignes de taux et du pont
 * catégorie → CP. Une CP peut recevoir plusieurs catégories (la 100 en reçoit
 * huit) : on les garde toutes, nommées, plutôt que d'en élire une.
 */
export function construireIndexCp(lignes, pontCategorieCp) {
  const parCategorie = new Map();
  for (const l of lignes || []) {
    if (!parCategorie.has(l.categorie)) parCategorie.set(l.categorie, []);
    parCategorie.get(l.categorie).push(l);
  }
  const index = {};
  for (const [categorie, cps] of Object.entries(pontCategorieCp || {})) {
    const lignesCat = parCategorie.get(categorie);
    if (!lignesCat || !lignesCat.length) continue;
    for (const cp of cps) {
      const cle = cleCp(cp);
      if (!index[cle]) index[cle] = [];
      index[cle].push({ categorie, lignes: lignesCat });
    }
  }
  for (const cle of Object.keys(index)) {
    index[cle].sort((a, b) => a.categorie.localeCompare(b.categorie));
  }
  return index;
}
